
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import eventService from './eventService'

const initialState = {
    events: [],
    eventsPast: [],
    eventsFront: [],
    eventUsers: [],
    eventAnnouncements: [],
    event: null,
    eventNotes: [],
    eventAgendas: [],
    loadingStates: {
        createEvent: false,
        getEvents: false,
        getEventsFront: false,
        getEventsPast: false,
        getSubEvents: false,
        getEvent: false,
        subscribeEvent: false,
        deleteEvent: false,
        updateEvent: false,
        getEventUsers: false,
        getEventAgendas: false,
        getEventAnnouncements: false,
        getEventNotes: false,
        createEventNote: false,
        joinEvent: false,
    },
    errors: {
        createEvent: null,
        getEvents: null,
        getEventsFront: null,
        getEventsPast: null,
        getSubEvents: null,
        getEvent: null,
        subscribeEvent: null,
        deleteEvent: null,
        updateEvent: null,
        getEventUsers: null,
        getEventAgendas: null,
        getEventAnnouncements: null,
        getEventNotes: null,
        createEventNote: null,
        joinEvent: null,
    },
    isError: false,
    isSuccess: false,
    isLoading: false,
    message: ''
}

// Thunk'lar createSlice'tan önce tanımlanmalı (TDZ fix)
export const createEvent = createAsyncThunk('event/createEvent', async (eventData, thunkAPI) => {
    try {
        const user = thunkAPI.getState().auth.user
        return await eventService.createEvent(eventData, user)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const getEvents = createAsyncThunk('event/getEvents', async (_, thunkAPI) => {
    try {
        const userId = thunkAPI.getState().auth.user.uid
        return await eventService.getEvents(userId)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const getEventsFront = createAsyncThunk('event/getEventsFront', async (_, thunkAPI) => {
    try {
        return await eventService.getEventsFront()
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const getEventsPast = createAsyncThunk('event/getEventsPast', async (_, thunkAPI) => {
    try {
        return await eventService.getEventsPast()
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const getSubEvents = createAsyncThunk('event/getSubEvents', async (eventId, thunkAPI) => {
    try {
        return await eventService.getSubEvents(eventId)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const getEvent = createAsyncThunk('event/getEvent', async (id, thunkAPI) => {
    try {
        return await eventService.getEvent(id)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const subscribeEvent = createAsyncThunk(
    'event/subscribeEvent',
    async (eventId, thunkAPI) => {
        try {
            const unsubscribe = eventService.subscribeEvent(eventId, (data) => {
                thunkAPI.dispatch(setEvent(data))
            })
            return unsubscribe
        } catch (error) {
            const message = error.message
            return thunkAPI.rejectWithValue(message)
        }
    }
)

export const deleteEvent = createAsyncThunk('event/deleteEvent', async (id, thunkAPI) => {
    try {
        return await eventService.deleteEvent(id)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const updateEvent = createAsyncThunk('event/updateEvent', async ({ eventData, id }, thunkAPI) => {
    try {
        return await eventService.updateEvent(eventData, id)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const getEventUsers = createAsyncThunk('event/getEventUsers', async (eventId, thunkAPI) => {
    try {
        return await eventService.getEventUsers(eventId)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const getEventAgendas = createAsyncThunk('event/getEventAgendas', async (eventId, thunkAPI) => {
    try {
        return await eventService.getEventAgendas(eventId)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})


export const getEventAnnouncements = createAsyncThunk('event/getEventAnnouncements', async (eventId, thunkAPI) => {
    try {
        return await eventService.getEventAnnouncements(eventId)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const getEventNotes = createAsyncThunk('event/getEventNotes', async (eventId, thunkAPI) => {
    try {
        const userId = thunkAPI.getState().auth.user.uid
        return await eventService.getEventNotes(eventId, userId)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const createEventNote = createAsyncThunk('event/createEventNote', async (noteData, thunkAPI) => {
    try {
        const userId = thunkAPI.getState().auth.user.uid
        return await eventService.createEventNote(noteData, noteData.eventId, userId)
    } catch (error) {
        const message = error.message;
        return thunkAPI.rejectWithValue(message);
    }
})

export const joinEvent = createAsyncThunk('event/etkinligekatil', async (eventId, thunkAPI) => {
    try {
        const userId = thunkAPI.getState().auth.user.uid
        return await eventService.joinEvent(eventId, userId)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const eventSlice = createSlice({
    name: 'event',
    initialState,
    reducers: {
        reset: (state) => initialState,
        setEvent: (state, action) => {
            state.event = action.payload
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(createEvent.pending, (state) => {
                state.isLoading = true
                state.loadingStates.createEvent = true
                state.errors.createEvent = null
            })
            .addCase(createEvent.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.isError = false
                state.events.unshift(action.payload)
                state.loadingStates.createEvent = false
                state.errors.createEvent = null
            })
            .addCase(createEvent.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.createEvent = false
                state.errors.createEvent = action.payload
            })

            .addCase(getEvents.pending, (state) => {
                state.isLoading = true
                state.loadingStates.getEvents = true
                state.errors.getEvents = null
            })
            .addCase(getEvents.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.events = action.payload
                state.isError = false
                state.loadingStates.getEvents = false
                state.errors.getEvents = null
            })
            .addCase(getEvents.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.getEvents = false
                state.errors.getEvents = action.payload
            })

            .addCase(getSubEvents.pending, (state) => {
                state.isLoading = true
                state.loadingStates.getSubEvents = true
                state.errors.getSubEvents = null
            })
            .addCase(getSubEvents.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.events = action.payload
                state.isError = false
                state.loadingStates.getSubEvents = false
                state.errors.getSubEvents = null
            })
            .addCase(getSubEvents.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.getSubEvents = false
                state.errors.getSubEvents = action.payload
            })

            .addCase(deleteEvent.pending, (state) => {
                state.isLoading = true
                state.loadingStates.deleteEvent = true
                state.errors.deleteEvent = null
            })
            .addCase(deleteEvent.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.events = state.events.filter((event) => event.id !== action.payload)
                state.isError = false
                state.loadingStates.deleteEvent = false
                state.errors.deleteEvent = null
            })
            .addCase(deleteEvent.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.deleteEvent = false
                state.errors.deleteEvent = action.payload
            })

            .addCase(updateEvent.pending, (state) => {
                state.isLoading = true
                state.loadingStates.updateEvent = true
                state.errors.updateEvent = null
            })
            .addCase(updateEvent.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.isError = false
                state.loadingStates.updateEvent = false
                state.errors.updateEvent = null
                const index = state.events.findIndex(e => e.id === action.payload.id)
                if (index !== -1) {
                    state.events[index] = action.payload
                }
            })
            .addCase(updateEvent.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.updateEvent = false
                state.errors.updateEvent = action.payload
            })

            .addCase(getEvent.pending, (state) => {
                state.isLoading = true
                state.isSuccess = false
                state.loadingStates.getEvent = true
                state.errors.getEvent = null
            })
            .addCase(getEvent.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.isError = false
                state.event = action.payload
                state.loadingStates.getEvent = false
                state.errors.getEvent = null
            })
            .addCase(getEvent.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.getEvent = false
                state.errors.getEvent = action.payload
            })

            .addCase(subscribeEvent.pending, (state) => {
                state.loadingStates.subscribeEvent = true
                state.errors.subscribeEvent = null
            })
            .addCase(subscribeEvent.fulfilled, (state) => {
                state.loadingStates.subscribeEvent = false
                state.errors.subscribeEvent = null
            })
            .addCase(subscribeEvent.rejected, (state, action) => {
                state.loadingStates.subscribeEvent = false
                state.errors.subscribeEvent = action.payload
            })

            .addCase(getEventUsers.pending, (state) => {
                state.isLoading = true
                state.loadingStates.getEventUsers = true
                state.errors.getEventUsers = null
            })
            .addCase(getEventUsers.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.eventUsers = action.payload
                state.isError = false
                state.loadingStates.getEventUsers = false
                state.errors.getEventUsers = null
            })
            .addCase(getEventUsers.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.getEventUsers = false
                state.errors.getEventUsers = action.payload
            })

            .addCase(getEventAgendas.pending, (state) => {
                state.isLoading = true
                state.loadingStates.getEventAgendas = true
                state.errors.getEventAgendas = null
            })
            .addCase(getEventAgendas.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.eventAgendas = action.payload
                state.isError = false
                state.loadingStates.getEventAgendas = false
                state.errors.getEventAgendas = null
            })
            .addCase(getEventAgendas.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.getEventAgendas = false
                state.errors.getEventAgendas = action.payload
            })

            .addCase(getEventNotes.pending, (state) => {
                state.isLoading = true
                state.loadingStates.getEventNotes = true
                state.errors.getEventNotes = null
            })
            .addCase(getEventNotes.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.eventNotes = action.payload
                state.isError = false
                state.loadingStates.getEventNotes = false
                state.errors.getEventNotes = null
            })
            .addCase(getEventNotes.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.getEventNotes = false
                state.errors.getEventNotes = action.payload
            })

            .addCase(createEventNote.pending, (state) => {
                state.isLoading = true
                state.loadingStates.createEventNote = true
                state.errors.createEventNote = null
            })
            .addCase(createEventNote.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.isError = false
                state.eventNotes.unshift(action.payload)
                state.loadingStates.createEventNote = false
                state.errors.createEventNote = null
            })
            .addCase(createEventNote.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.createEventNote = false
                state.errors.createEventNote = action.payload
            })


            .addCase(joinEvent.pending, (state) => {
                state.isLoading = true
                state.loadingStates.joinEvent = true
                state.errors.joinEvent = null
            })
            .addCase(joinEvent.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.isError = false
                state.loadingStates.joinEvent = false
                state.errors.joinEvent = null
            })
            .addCase(joinEvent.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.joinEvent = false
                state.errors.joinEvent = action.payload
            })

            .addCase(getEventsFront.pending, (state) => {
                state.isLoading = true
                state.loadingStates.getEventsFront = true
                state.errors.getEventsFront = null
            })
            .addCase(getEventsFront.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.eventsFront = action.payload
                state.isError = false
                state.loadingStates.getEventsFront = false
                state.errors.getEventsFront = null
            })
            .addCase(getEventsFront.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.getEventsFront = false
                state.errors.getEventsFront = action.payload
            })


            .addCase(getEventsPast.pending, (state) => {
                state.isLoading = true
                state.loadingStates.getEventsPast = true
                state.errors.getEventsPast = null
            })
            .addCase(getEventsPast.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.eventsPast = action.payload
                state.isError = false
                state.loadingStates.getEventsPast = false
                state.errors.getEventsPast = null
            })
            .addCase(getEventsPast.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.getEventsPast = false
                state.errors.getEventsPast = action.payload
            })


            .addCase(getEventAnnouncements.pending, (state) => {
                state.isLoading = true
                state.loadingStates.getEventAnnouncements = true
                state.errors.getEventAnnouncements = null
            })
            .addCase(getEventAnnouncements.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.eventAnnouncements = action.payload
                state.isError = false
                state.loadingStates.getEventAnnouncements = false
                state.errors.getEventAnnouncements = null
            })
            .addCase(getEventAnnouncements.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.getEventAnnouncements = false
                state.errors.getEventAnnouncements = action.payload
            })

    }
})

export const { reset, setEvent } = eventSlice.actions
export default eventSlice.reducer
